// D4 (QA-29): connectivity must stay visible on EVERY screen — including the
// crash fallbacks. PRESENTATIONAL: callers supply `isOnline`, so normal pages
// ride the app-wide OnlineStatusContext signal and exactly ONE useIsOnlineCore
// heartbeat/navigator-override owner exists (the provider's — see the hook's
// provider-only-once note). Renders nothing unless definitely offline.
import { useIsOnlineCore } from '@/hooks/utils/useIsOnlineCore';
import { cn } from '@/lib/cn';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface OfflineStatusBannerProps {
  /** Connectivity signal — the OnlineStatusContext value on normal pages. */
  isOnline: boolean;
  /** Positioning classes — callers own placement (sticky/fixed/flow). */
  className?: string;
  /** Optional trailing affordance (e.g. the mac-offline HowToFix button). */
  action?: ReactNode;
}

export const OfflineStatusBanner = ({
  isOnline,
  className = '',
  action,
}: OfflineStatusBannerProps) => {
  const { t } = useTranslation();
  // Strict false only: an undefined context read (dead provider) must not
  // flash the banner (the v3 `typeof isOnline === 'undefined'` AAC guard).
  if (isOnline !== false) return null;
  return (
    <div
      // forced-colors maps --ap-warning-a15 to Canvas — the tint vanishes, so
      // give the banner a real edge there (--border: CanvasText in the FC block).
      className={cn(
        'flex w-full items-center justify-center gap-3 bg-ap-warning-a15 px-4 py-2 text-sm text-foreground forced-colors:border-b forced-colors:border-border',
        className
      )}
      role="alert"
      data-testid="internet-alert-banner"
    >
      <span>{t('CommonText.it_seems_you_are_not_connected_to_the_internet')}</span>
      {action}
    </div>
  );
};

// The ROOT crash fallback (AppErrorBoundary) replaces the whole tree —
// OnlineStatusContext's provider is dead there, so this wrapper owns its own
// useIsOnlineCore read. It is the sole legitimate second instance of the
// hook: it exists only while the root crash screen is up.
export const SelfContainedOfflineStatusBanner = ({
  className,
  action,
}: Omit<OfflineStatusBannerProps, 'isOnline'>) => {
  const { isOnline } = useIsOnlineCore();
  return <OfflineStatusBanner isOnline={isOnline} className={className} action={action} />;
};
